import React, { useEffect, useContext, useState, useCallback } from "react";
import config from "config";
import { closeSocket } from "../../../src/Dashboard/ducks/socketDuck";
import Socket from "Socket/socket.js";
import {
  alarmsMessageTypeEvent,
} from "Constants/apiUrl";
import { MsalContext } from "Auth/msal-context";
import { useDispatch, useSelector } from "react-redux";
import { messengerRequest } from "Auth/auth-config";
import { groupBy } from "../../../src/Dashboard/helpers/utils";
import {
  setNetworkConnection,
  getConfig,
} from "../../../src/Dashboard/ducks/systemDuck";
import { fetchDevicesConfigs } from "../../../src/Dashboard/ducks/devicesConfigsDuck";
import { upsertAlarmsForMap } from "../../../src/Dashboard/ducks/alarmsDuck";
import { useSocketConnectionDevicesMessageTypeEvent, useSocketConnectionStateChangedUpdateEvent } from '../../../src/Dashboard/helpers/useSocketConnection';
import { useCloudSocketConnected, useCloudSystemConfig, useCloudIsNetworkConnected, useCloudDevicesList, useCloudDevicesConfigs, useCloudSolutionType } from '../../hooks/useCloudSelectors';

const SingleSystem = ({ system, setSystemsWithAlarms }) => {
  const dispatch = useDispatch();
  const msalContext = useContext(MsalContext);
  const isConfigInitialized = useSelector(({ config: c }) => c[system.deviceId]?._initialized);
  const isRealtimeInitialized = useSelector(({ realtime }) => realtime[system.deviceId]?._initialized);

  const socketConnected = useCloudSocketConnected(system.deviceId);
  const systemConfig = useCloudSystemConfig(system.deviceId);
  const isNetworkConnected = useCloudIsNetworkConnected(system.deviceId);
  const devicesList = useCloudDevicesList(system.deviceId);
  const devicesConfigs = useCloudDevicesConfigs(system.deviceId);
  const solutionType = useCloudSolutionType(system.deviceId);

  const [allAlarms, setAllAlarms] = useState([]);

  // Initialize device state in Redux tiers (same as Dashboard.jsx does)
  useEffect(() => {
    const payload = { systemId: system.deviceId, solutionType: system.solutionType };
    if (isConfigInitialized === false) {
      dispatch({ type: "config/initializeDeviceConfig", payload });
    }
    if (isRealtimeInitialized === false) {
      dispatch({ type: "realtime/initializeDeviceRealtime", payload });
    }
  }, [system.deviceId, isConfigInitialized, isRealtimeInitialized, dispatch]);

  // Socket event listeners — use shared hooks that batch messages into arrays
  useSocketConnectionDevicesMessageTypeEvent(socketConnected, system.deviceId, dispatch);
  useSocketConnectionStateChangedUpdateEvent(socketConnected, system.deviceId, dispatch);

  const handleConnectionChange = () => {
    const condition = navigator.onLine;
    const payload = {
      isNetworkConnected: condition,
      systemId: system.deviceId,
    };

    dispatch(setNetworkConnection(payload));
  };

  const handleMessages = useCallback((item) => {
    const newItem = {
      alarmId: item.AlarmId,
      hwId: item.HwId,
      displayName: item.ValueName,
      sourceTimestamp: item.SourceTimestamp,
      triggerTimeStamp: item.TriggerTimeStamp,
      turnedOffTimeStamp: item.TurnedOffTimeStamp,
      isActive: item.TurnedOffTimeStamp === 0,
      severity: item.Severity,
      category: item.Category,
      brief: item.Brief,
      descriptionFI: item.DescriptionFI,
      descriptionEN: item.DescriptionEN,
    };
    setAllAlarms((prev) => {
      let alarmsArray = [...prev];
      const index = alarmsArray.findIndex(function (o) {
        return o.alarmId === item.AlarmId && o.hwId === item.HwId;
      });
      if (index >= 0) {
        alarmsArray.splice(index, 1, newItem);
      } else {
        alarmsArray.push(newItem);
      }
      return alarmsArray;
    });
  }, []);

  const fetchAlarmsSocketIo = useCallback(() => {
    if (Socket) {
      Socket.on(alarmsMessageTypeEvent, (res) => {
        if (res && system.deviceId === res.DeviceId) {
          handleMessages(res);
        }
      }, system.deviceId);
    }
  }, [Socket, handleMessages, system]);

  const calculateColor = useCallback((list) => {
    if (list.some((item) => item?.deviceBgColor === "red")) {
      return "red";
    } else if (list.some((item) => item?.deviceBgColor === "orange")) {
      return "orange";
    } else {
      return "green";
    }
  }, []);

  useEffect(() => {
    msalContext.msalExec(messengerRequest, (accessToken) => {
      if (config) {
        dispatch(
          getConfig({
            systemId: system.deviceId,
            data: {
              ...config,
              CUSTOMER_ID: system.customerId,
              DEVICE_ID: system.deviceId,
              CONFIG_FILE: system.config,
              accessToken,
              user: { homeAccountId: msalContext.user.homeAccountId },
            },
          })
        );
      }
    });

    handleConnectionChange();
    window.addEventListener("online", handleConnectionChange);
    window.addEventListener("offline", handleConnectionChange);

    return () => {
      window.removeEventListener("online", handleConnectionChange);
      window.removeEventListener("offline", handleConnectionChange);
      Socket.removeListener(alarmsMessageTypeEvent, system.deviceId);
      dispatch(closeSocket({ systemId: system.deviceId }));
    };
  }, [dispatch]);

  useEffect(() => {
    if (systemConfig && Object.keys(systemConfig).length > 0 && isNetworkConnected) {
      dispatch(
        fetchDevicesConfigs({
          systemId: system.deviceId,
          systemConfig: systemConfig,
          isSensor: solutionType === "lora" ? true : false
        })
      );
    }
  }, [systemConfig, isNetworkConnected]);

  useEffect(() => {
    if (socketConnected) {
      fetchAlarmsSocketIo();
    }
  }, [socketConnected, fetchAlarmsSocketIo]);

  useEffect(() => {
    const data = groupBy(allAlarms, (o) => o.hwId);
    dispatch(upsertAlarmsForMap({ data, systemId: system.deviceId }));
  }, [allAlarms, dispatch]);

  useEffect(() => {
    if (devicesList) {
      const color = calculateColor(devicesList);
      setSystemsWithAlarms((prev) => {
        return {
          ...prev,
          [system.deviceId]: color,
        };
      });
    }
  }, [devicesList, calculateColor]);

  return <></>;
};

export default SingleSystem;
